import {
  CLEAR_CURATED_VEHICLE_DETAILS_SUCCESS,
  CuratedVehiclesAction, GET_DOWN_PAYMENT_HISTORIES_SUCCESS,
  LOAD_CURATED_APPLICATION_DETAILS,
  LOAD_CURATED_APPLICATION_DETAILS_SUCCESS,
  LOAD_CURATED_APPLICATION_DETAILS_FAILURE,
  LOAD_CURATED_VEHICLE_DETAILS,
  LOAD_CURATED_VEHICLE_DETAILS_SUCCESS,
  LOAD_CURATED_VEHICLES,
  LOAD_CURATED_VEHICLES_SUCCESS,
  SET_VEHICLE_FILTERS_SUCCESS
} from './actions';
import {combineReducers} from 'redux';
import {PagedCuratedVehiclesAndLookupItems} from "../../Models/PagedCuratedVehiclesAndLookupItems";
import {
  PagedWebViewVehicles, VehicleWebViewFiltersWithPaging,
  WebViewVehicle, ApplicationDownPaymentResponse
} from "@amsanalytics/react-shared-library";
import {CuratedSiteLoadingDetail} from "../../Models/CuratedSiteLoadingDetail";
import {CuratedSiteApplicationFilters} from "../../Models/CuratedSiteApplicationFilters";
import {getDefaultFilters} from "../../Components/Pages/CuratedVehiclesPage/CuratedVehiclesUtils";
import {DownPaymentThankYouPageConfig} from "../../Models/CuratedSiteApplicationConfig";

export interface CuratedVehicleState {
  loading: CuratedSiteLoadingDetail;
  vehicles: PagedCuratedVehiclesAndLookupItems;
  application: CuratedSiteApplicationFilters | null;
  selectedVehicle: WebViewVehicle | null;
  downPaymentHistories: ApplicationDownPaymentResponse[];
  thankYouPageConfig: DownPaymentThankYouPageConfig | null;
}

const INITIAL_STATE: CuratedVehicleState = {
  loading: {
    vehiclesLoaded: false,
    selectedVehicleLoaded: false,
    applicationDetailsLoaded: false,
    applicationDetailsLoadFailed: false
  },
  vehicles: {
    result: [],
    total: 0,
    makes: [],
    models: [],
    carOrTrucks: []
  },
  downPaymentHistories: [],
  application: null,
  selectedVehicle: null,
  thankYouPageConfig: null,
};

const loadingReducer = (state: CuratedSiteLoadingDetail = INITIAL_STATE.loading, action: CuratedVehiclesAction): CuratedSiteLoadingDetail => {
  switch (action.type) {
    case LOAD_CURATED_VEHICLES:
      return {
        ...state,
        vehiclesLoaded: false
      };
    case LOAD_CURATED_VEHICLES_SUCCESS:
      return {
        ...state,
        vehiclesLoaded: true
      };
    case LOAD_CURATED_VEHICLE_DETAILS:
      return {
        ...state,
        selectedVehicleLoaded: false
      };
    case LOAD_CURATED_VEHICLE_DETAILS_SUCCESS:
      return {
        ...state,
        selectedVehicleLoaded: true
      };
    case LOAD_CURATED_APPLICATION_DETAILS:
      return {
        ...state,
        applicationDetailsLoaded: false,
        applicationDetailsLoadFailed: false
      };
    case LOAD_CURATED_APPLICATION_DETAILS_SUCCESS:
      return {
        ...state,
        applicationDetailsLoaded: true,
        applicationDetailsLoadFailed: false
      };
    case LOAD_CURATED_APPLICATION_DETAILS_FAILURE:
      return {
        ...state,
        applicationDetailsLoadFailed: true
      };
    default:
      return state;
  }
}

const applicationReducer = (state: CuratedSiteApplicationFilters | null = INITIAL_STATE.application, action: CuratedVehiclesAction): CuratedSiteApplicationFilters | null => {
  switch (action.type) {
    case LOAD_CURATED_APPLICATION_DETAILS_SUCCESS:
      const filtersFromServer: VehicleWebViewFiltersWithPaging = getDefaultFilters(action.payload);
      return {
        applicationId: action.payload.applicationId,
        wasApproved: action.payload.wasApproved,
        breakevenMonths: action.payload.breakevenMonths,
        overhead: action.payload.overhead,
        showVehicleLength: action.payload.showVehicleLength,
        slidesOrDoors: action.payload.slidesOrDoors,
        selectedFilters: filtersFromServer,
        defaultFilters: filtersFromServer,
        minDownPayment: action.payload.minDown,
        maxDownPayment: action.payload.maxDown,
        minMonthlyPayment: action.payload.minMonthlyPayment,
        maxMonthlyPayment: Math.max(action.payload.maxMonthlyPayment, action.payload.minMonthlyPayment), // making sure the max is at least the min
        fixedDepositAmount: action.payload.fixedDepositAmount,
        makeDepositButtonText: action.payload.makeDepositButtonText,
        impel360ScriptUrl: action.payload.impel360ScriptUrl,
        chatbotScriptUrl: action.payload.chatbotScriptUrl,
        paymentPercentOfMonthlyIncome: action.payload.paymentPercentOfMonthlyIncome,
        enableGuidedTour: action.payload.enableGuidedTour,
        guidedTourWelcomeMessage: action.payload.guidedTourWelcomeMessage,
        quoteFailedMessage: action.payload.quoteFailedMessage ?? null,
      };
    case SET_VEHICLE_FILTERS_SUCCESS:
      return state
      ? {
          ...state,
          selectedFilters: action.payload
        }
      : state;
    default:
      return state;
  }
}

const vehiclesReducer = (state: PagedCuratedVehiclesAndLookupItems = INITIAL_STATE.vehicles, action: CuratedVehiclesAction): PagedWebViewVehicles => {
  switch (action.type) {
    case LOAD_CURATED_VEHICLES_SUCCESS:
      return action.payload;
    default:
      return state;
  }
}

const selectedVehicleReducer = (state: WebViewVehicle | null = INITIAL_STATE.selectedVehicle, action: CuratedVehiclesAction): WebViewVehicle | null => {
  switch (action.type) {
    case LOAD_CURATED_VEHICLE_DETAILS_SUCCESS:
      return action.payload;
    case CLEAR_CURATED_VEHICLE_DETAILS_SUCCESS:
      return null;
    default:
      return state;
  }
}

const downPaymentHistoryReducer = (state: ApplicationDownPaymentResponse[] = INITIAL_STATE.downPaymentHistories, action: CuratedVehiclesAction): ApplicationDownPaymentResponse[] => {
  switch (action.type) {
    case GET_DOWN_PAYMENT_HISTORIES_SUCCESS:
      return action.payload;
    default:
      return state;
  }
}

const thankYouPageConfigReducer = (state: DownPaymentThankYouPageConfig | null = INITIAL_STATE.thankYouPageConfig, action: CuratedVehiclesAction): DownPaymentThankYouPageConfig | null => {
  switch (action.type) {
    case LOAD_CURATED_APPLICATION_DETAILS_SUCCESS:
      return {
        downPaymentThankYouButtonText: action.payload.downPaymentThankYouButtonText,
        downPaymentThankYouRedirectLink: action.payload.downPaymentThankYouRedirectLink
      };
    default:
      return state;
  }
}

export const curatedVehiclesReducer = combineReducers({
  loading: loadingReducer,
  application: applicationReducer,
  vehicles: vehiclesReducer,
  selectedVehicle: selectedVehicleReducer,
  downPaymentHistories: downPaymentHistoryReducer,
  thankYouPageConfig: thankYouPageConfigReducer,
});
